import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRightLeft, Package, Undo2 } from 'lucide-react';
import { ASSET_BRANCH_CODES, ASSET_BRANCH_TRANSFER_STATES, type AssetBranchCode } from '@pms/shared';
import { assetsService, type AssetBranchTransfer } from '@/services/assets.service';
import { formatDateTimeIST } from '@/lib/dateFormat';
import { Button } from '@/components/ui/button';
import { DataTable, type Column } from '@/components/ui/data-table';
import { SearchInput } from '@/components/ui/search-input';
import { Badge } from '@/components/ui/badge';
import { AuthImage } from '@/components/ui/auth-image';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ReturnAssetDialog } from './components/ReturnAssetDialog';

const PAGE_LIMIT = 20;
const SEARCH_DEBOUNCE_MS = 350;
const ALL = '__all__';

const BRANCH_BADGE: Record<AssetBranchCode, string> = {
  main: 'bg-sky-50 text-sky-700 border-sky-200',
  couture: 'bg-violet-50 text-violet-700 border-violet-200',
  prod: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const BRANCH_LABEL: Record<AssetBranchCode, string> = { main: 'Main', couture: 'Couture', prod: 'Prod' };

type TransferState = (typeof ASSET_BRANCH_TRANSFER_STATES)[number];

const STATE_LABEL: Record<TransferState, string> = { at_branch: 'At branch', returned: 'Returned' };

function isTransferState(v: string | null): v is TransferState {
  return !!v && (ASSET_BRANCH_TRANSFER_STATES as readonly string[]).includes(v);
}

/** Where the asset is now: live from the branch DB while away, or the return record. */
function StatusThere({ row }: { row: AssetBranchTransfer }) {
  if (row.returned_at) {
    return (
      <div>
        <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600">Returned</Badge>
        <p className="mt-1 text-xs text-muted-foreground">
          {formatDateTimeIST(row.returned_at)}{row.returned_by_name && ` · by ${row.returned_by_name}`}
        </p>
        {row.return_notes && <p className="text-xs text-muted-foreground line-clamp-1" title={row.return_notes}>{row.return_notes}</p>}
      </div>
    );
  }
  if (row.live_unavailable || !row.live) {
    return <span className="text-xs text-muted-foreground">Branch database unreachable</span>;
  }
  const { live } = row;
  if (live.state === 'removed') {
    return <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700">Deleted there</Badge>;
  }
  return (
    <div>
      {live.state === 'assigned' ? (
        <p className="text-sm font-medium">{live.user_name}</p>
      ) : (
        <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">In stock</Badge>
      )}
      <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
        {live.status && (
          <>
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: live.status.color }} />
            {live.status.name}
          </>
        )}
        {live.department_name && <span>· {live.department_name}</span>}
      </p>
    </div>
  );
}

function isBranchCode(v: string | null): v is AssetBranchCode {
  return !!v && (ASSET_BRANCH_CODES as readonly string[]).includes(v);
}

export function AssetBranchTransfersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const branchParam = searchParams.get('branch');
  const branch = isBranchCode(branchParam) ? branchParam : undefined;
  const qParam = searchParams.get('q') ?? '';
  const stateParam = searchParams.get('state');
  const state = isTransferState(stateParam) ? stateParam : undefined;

  const [q, setQ] = useState(qParam);
  const [returnTarget, setReturnTarget] = useState<AssetBranchTransfer | null>(null);

  const updateParams = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v); else next.delete(k);
    }
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (q === qParam) return;
    const t = setTimeout(() => updateParams({ q: q || undefined, page: undefined }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // updateParams is recreated each render; q is the only trigger that matters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const params = { page, limit: PAGE_LIMIT, branch, state, q: qParam || undefined };
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['assets', 'branch-transfers', params],
    queryFn: () => assetsService.listBranchTransfers(params),
  });

  const columns: Column<AssetBranchTransfer>[] = [
    {
      key: 'photo',
      header: 'Photo',
      className: 'w-16',
      cell: (row) => {
        const placeholder = (
          <div className="flex h-10 w-10 items-center justify-center rounded bg-muted">
            <Package className="h-5 w-5 text-muted-foreground" />
          </div>
        );
        return row.photo_doc_id ? (
          <AuthImage
            src={`/assets/documents/${row.photo_doc_id}/file`}
            alt={row.asset_code}
            className="h-10 w-10 rounded object-cover"
            fallback={placeholder}
          />
        ) : placeholder;
      },
    },
    {
      key: 'asset_code',
      header: 'Asset Code',
      cell: (row) => <code className="text-xs font-mono font-semibold">{row.asset_code}</code>,
    },
    {
      key: 'asset',
      header: 'Asset',
      cell: (row) => (
        <div>
          <p className="text-sm">
            {row.category_name ?? '—'}
            {row.subcategory_name && <span className="text-muted-foreground"> · {row.subcategory_name}</span>}
          </p>
          {(row.brand_name || row.model_name) && (
            <p className="text-xs text-muted-foreground">
              {[row.brand_name, row.model_name].filter(Boolean).join(' / ')}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'branch',
      header: 'Branch',
      cell: (row) => (
        <Badge variant="outline" className={BRANCH_BADGE[row.branch.code]}>{row.branch.label}</Badge>
      ),
    },
    {
      key: 'status_there',
      header: 'Status There',
      cell: (row) => <StatusThere row={row} />,
    },
    {
      key: 'transferred',
      header: 'Transferred',
      cell: (row) => (
        <div>
          <p className="text-sm">{formatDateTimeIST(row.transferred_at)}</p>
          {row.transferred_by_name && (
            <p className="text-xs text-muted-foreground">by {row.transferred_by_name}</p>
          )}
          {row.target_user_name && (
            <p className="text-xs text-muted-foreground">sent to {row.target_user_name}</p>
          )}
        </div>
      ),
    },
    {
      key: 'notes',
      header: 'Notes',
      className: 'max-w-xs',
      cell: (row) => (
        <p className="line-clamp-2 text-sm text-muted-foreground" title={row.notes ?? undefined}>
          {row.notes ?? '—'}
        </p>
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      className: 'text-right',
      cell: (row) => row.returned_at ? null : (
        <Button size="sm" variant="outline" onClick={() => setReturnTarget(row)}>
          <Undo2 className="h-4 w-4" /> Return
        </Button>
      ),
    },
  ];

  const hasFilters = !!branch || !!state || !!qParam;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <ArrowRightLeft className="h-6 w-6 text-primary" />
          Other Branch Assets
        </h1>
        <p className="text-sm text-muted-foreground">
          Assets sent from this company to another branch, with where each one is there right now. Send one with the transfer button on Assets → List; Return brings it back to stock.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={q}
          onChange={setQ}
          placeholder="Search code, model, user, department…"
          containerClassName="min-w-[200px] flex-1 max-w-xs"
        />
        <Select
          value={branch ?? ALL}
          onValueChange={(v) => updateParams({ branch: v === ALL ? undefined : v, page: undefined })}
        >
          <SelectTrigger className="h-9 w-40">
            <SelectValue placeholder="All Branches" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All Branches</SelectItem>
            {ASSET_BRANCH_CODES.map((c) => (
              <SelectItem key={c} value={c}>{BRANCH_LABEL[c]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={state ?? ALL}
          onValueChange={(v) => updateParams({ state: v === ALL ? undefined : v, page: undefined })}
        >
          <SelectTrigger className="h-9 w-40">
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>At branch + returned</SelectItem>
            {ASSET_BRANCH_TRANSFER_STATES.map((st) => (
              <SelectItem key={st} value={st}>{STATE_LABEL[st]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {data && (
          <span className="ml-auto text-xs text-muted-foreground">
            {data.pagination.total} {data.pagination.total === 1 ? 'asset' : 'assets'}
          </span>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        loading={isLoading}
        error={isError}
        onRetry={() => void refetch()}
        empty={hasFilters ? 'No transferred assets match the current filters' : 'No assets have been transferred to another branch yet'}
        pagination={data ? {
          page: data.pagination.page,
          totalPages: data.pagination.totalPages,
          total: data.pagination.total,
          onPageChange: (p: number) => updateParams({ page: p > 1 ? String(p) : undefined }),
        } : undefined}
      />

      <ReturnAssetDialog
        key={returnTarget?.id ?? 'none'}
        transfer={returnTarget}
        onClose={() => setReturnTarget(null)}
      />
    </div>
  );
}
